import { useTranslation } from "react-i18next";
import { useOutletContext, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EditTenderContext } from "@/layouts/EditTenderLayout";
import { BidDecisionGroup, DecisionEnum } from "@/graphql/__generated__/graphql";
import UpdateParticipantsStepperFormDialog from "./UpdateParticipantsStepperFormDialog/UpdateParticipantsStepperFormDialog";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useTenderPermissionsData } from "@/hooks/cache";

/**
 * OperationalOrganizations component displays a list of organizations that are participating in the tender.
 * Shows an edit button only for tender owners to modify participating organizations.
 *
 * @returns The rendered participating organizations component
 */
function OperationalOrganizations() {
  const { t } = useTranslation(["translation", "common"]);
  const { tenderByIdData } = useOutletContext<EditTenderContext>();
  const { tenderID } = useParams();
  const { canEditDetails } = useTenderPermissionsData(Number(tenderID));


  // Get participating organizations (those with Bid decision or awaiting organization)
  const bidDecisionGroups = tenderByIdData?.fetchTenderById?.bidDecisionGroups ?? [];

  const participatingGroups = bidDecisionGroups.filter(
    (group) =>
      group.currentDecision === DecisionEnum.Bid || group.awaitingOrganizationId !== null
  ) as BidDecisionGroup[];

  return (
    <Card>
      <CardHeader className="pt-4 pb-2 select-none">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">
            {t("edittender.details.operationalorganizations")}
          </CardTitle>
          {canEditDetails && participatingGroups.length > 0 && (
            <UpdateParticipantsStepperFormDialog participatingGroups={participatingGroups} />
          )}
        </div>
      </CardHeader>
      <CardContent>
        {bidDecisionGroups.length > 0 ? (
          <div className="space-y-2">
            {bidDecisionGroups.map((organization) => {
              const isBidding =
                organization.currentDecision === DecisionEnum.Bid &&
                organization.awaitingOrganizationId === null;
              const isNoBid =
                organization.currentDecision === DecisionEnum.Nobid &&
                organization.awaitingOrganizationId === null;
              return (
                <div
                  key={organization.id}
                  className="flex items-center gap-2 justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <span className="font-medium text-sm">
                    {organization.operationalOrganizationName}
                  </span>
                  <div className="flex items-center gap-2">
                    <Badge
                      className={cn("whitespace-normal text-center",
                        isBidding
                          ? "bg-green-100 text-green-800"
                          : isNoBid
                            ? "bg-red-100 text-red-800"
                            : "bg-yellow-100 text-yellow-800"
                      )}
                    >
                      {isBidding
                        ? t("edittender.details.bidding")
                        : isNoBid
                          ? t("edittender.details.nobid")
                          : t("edittender.details.pending")}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p className="text-sm">
              {t("edittender.details.nooperationalorganizations")}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default OperationalOrganizations;
